import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, Pin, Trash2, X, ArrowLeft, TrendingUp, Beaker, Trophy, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ["Revenue", "Marketing", "Sales / Conversion", "Operations", "Customer Experience", "Hiring / Team", "Automation", "Product / Service", "Cost Savings", "Risk / Compliance", "Other"];
const OWNERS = ["Brett", "Admin", "Neil", "Matt", "Other"];
const STATUSES = [
  { key: "inbox", label: "Inbox" },
  { key: "prioritized", label: "Prioritized" },
  { key: "doing", label: "Doing" },
  { key: "testing", label: "Testing" },
  { key: "done", label: "Done" },
];
const OUTCOMES = [
  { key: "big_win", label: "Big Win" },
  { key: "win", label: "Win" },
  { key: "neutral", label: "Neutral" },
  { key: "loss", label: "Loss" },
  { key: "inconclusive", label: "Inconclusive" },
];
const UNITS = ["Number", "$", "%", "Screens", "Jobs", "Leads", "Bookings", "Custom"];

const CATEGORY_COLOR = {
  "Revenue": "bg-emerald-100 text-emerald-700",
  "Marketing": "bg-pink-100 text-pink-700",
  "Sales / Conversion": "bg-blue-100 text-blue-700",
  "Operations": "bg-amber-100 text-amber-700",
  "Customer Experience": "bg-violet-100 text-violet-700",
  "Automation": "bg-cyan-100 text-cyan-700",
  "Product / Service": "bg-indigo-100 text-indigo-700",
  "Cost Savings": "bg-teal-100 text-teal-700",
};

const STATUS_COLOR = {
  inbox: "bg-zinc-100 text-zinc-700",
  prioritized: "bg-blue-100 text-blue-700",
  doing: "bg-amber-100 text-amber-700",
  testing: "bg-purple-100 text-purple-700",
  done: "bg-green-100 text-green-700",
};

const money = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
};

function ScoreInput({ label, value, onChange, hint }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-zinc-700">{label} <span className="text-zinc-400 font-normal">({hint})</span></Label>
      <Input type="number" min="1" max="10" step="1" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" placeholder="1–10" />
    </div>
  );
}

function IdeaCard({ idea, onOpen }) {
  const impact = money(idea.est_annual_impact);
  return (
    <button type="button" onClick={() => onOpen(idea)} className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3 hover:border-[#207024] hover:shadow-sm transition-all" data-testid={`growth-card-${idea.idea_id}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-900 text-sm truncate">{idea.title}</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {idea.pinned && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full"><Pin className="w-3 h-3" />PINNED</span>}
            {idea.category && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${CATEGORY_COLOR[idea.category] || "bg-zinc-100 text-zinc-700"}`}>{idea.category}</span>}
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${STATUS_COLOR[idea.status]}`}>{STATUSES.find(s => s.key === idea.status)?.label || idea.status}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-lg font-bold text-[#207024] leading-none">{idea.priority_score !== null ? idea.priority_score : <span className="text-xs text-zinc-400 font-normal">Not scored</span>}</div>
          {idea.priority_score !== null && <div className="text-[10px] text-zinc-500 uppercase mt-0.5">Priority</div>}
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs text-zinc-600">
        {idea.revenue_impact != null && <span>💰 {idea.revenue_impact}/10</span>}
        {idea.effort != null && <span>⚡ Effort {idea.effort}/10</span>}
        {impact && <span className="text-emerald-700 font-semibold">{impact}/yr</span>}
        {idea.owner && <span className="ml-auto text-zinc-500">{idea.owner}</span>}
      </div>
    </button>
  );
}

function IdeaModal({ idea, onClose, onSave, onDelete }) {
  const [form, setForm] = useState(idea);
  useEffect(() => { setForm(idea); }, [idea]);
  if (!form) return null;
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const showResults = form.status === "done" || form.status === "testing";
  const priority = (() => {
    const keys = ["revenue_impact", "confidence", "urgency", "effort", "cost"];
    if (keys.some(k => form[k] == null || form[k] === "")) return null;
    if (form.priority_score_override != null && form.priority_score_override !== "") return Number(form.priority_score_override);
    const [ri, cf, ur, ef, co] = keys.map(k => Number(form[k]));
    const raw = (ri * cf * 2) + (ur * 5) - (ef * 5) - (co * 3);
    return Math.round(Math.max(0, Math.min(100, ((raw + 73) / 315) * 100)) * 10) / 10;
  })();

  return (
    <Dialog open={!!idea} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="idea-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <span className="flex-1 min-w-0 truncate">{form.title || "New Idea"}</span>
            <button onClick={() => set("pinned", !form.pinned)} className={`text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 ${form.pinned ? "bg-amber-500 text-white" : "bg-zinc-100 text-zinc-500"}`} data-testid="pin-toggle"><Pin className="w-3 h-3" />{form.pinned ? "PINNED" : "Pin"}</button>
          </DialogTitle>
          <DialogDescription className="sr-only">Edit idea details</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="idea-title" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <select value={form.category || ""} onChange={(e) => set("category", e.target.value || null)} className="w-full h-10 rounded-md border border-zinc-200 px-3 text-sm mt-1">
                <option value="">—</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Owner</Label>
              <select value={form.owner || ""} onChange={(e) => set("owner", e.target.value || null)} className="w-full h-10 rounded-md border border-zinc-200 px-3 text-sm mt-1">
                <option value="">—</option>
                {OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label>Status</Label>
            <div className="grid grid-cols-5 gap-1 mt-1">
              {STATUSES.map(s => (
                <button key={s.key} type="button" onClick={() => set("status", s.key)} className={`py-2 text-xs font-semibold rounded-md border ${form.status === s.key ? "bg-[#207024] text-white border-[#207024]" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`} data-testid={`status-${s.key}`}>{s.label}</button>
              ))}
            </div>
          </div>

          <div>
            <Label>Description / Notes</Label>
            <Textarea value={form.description || form.notes || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1" />
          </div>

          <div className="bg-zinc-50 rounded-lg p-3 border border-zinc-200">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-zinc-900">Priority Scoring</div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#207024] leading-none">{priority !== null ? priority : <span className="text-sm text-zinc-400 font-normal">Not Prioritized</span>}</div>
                {priority !== null && <div className="text-[10px] text-zinc-500 uppercase mt-0.5">Priority Score</div>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ScoreInput label="Revenue Impact" hint="1–10" value={form.revenue_impact} onChange={(v) => set("revenue_impact", v)} />
              <ScoreInput label="Confidence" hint="1–10" value={form.confidence} onChange={(v) => set("confidence", v)} />
              <ScoreInput label="Urgency" hint="1–10" value={form.urgency} onChange={(v) => set("urgency", v)} />
              <ScoreInput label="Effort" hint="lower = easier" value={form.effort} onChange={(v) => set("effort", v)} />
              <ScoreInput label="Cost" hint="lower = cheaper" value={form.cost} onChange={(v) => set("cost", v)} />
            </div>
          </div>

          <div className="bg-zinc-50 rounded-lg p-3 border border-zinc-200">
            <div className="text-sm font-semibold text-zinc-900 mb-3">Financial Impact <span className="text-xs text-zinc-500 font-normal">(planning only)</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Est. Monthly Revenue</Label><Input type="number" value={form.est_monthly_revenue ?? ""} onChange={(e) => set("est_monthly_revenue", e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" /></div>
              <div><Label className="text-xs">Est. Annual Revenue</Label><Input type="number" value={form.est_annual_revenue ?? ""} onChange={(e) => set("est_annual_revenue", e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" /></div>
              <div><Label className="text-xs">Est. Monthly Savings</Label><Input type="number" value={form.est_monthly_savings ?? ""} onChange={(e) => set("est_monthly_savings", e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" /></div>
              <div><Label className="text-xs">Est. Annual Savings</Label><Input type="number" value={form.est_annual_savings ?? ""} onChange={(e) => set("est_annual_savings", e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" /></div>
            </div>
            {money(form.est_annual_impact) && <div className="mt-3 text-sm text-emerald-700 font-bold">Estimated Annual Impact: {money(form.est_annual_impact)}</div>}
          </div>

          <div className="bg-zinc-50 rounded-lg p-3 border border-zinc-200">
            <div className="text-sm font-semibold text-zinc-900 mb-3">Success Metric <span className="text-xs text-zinc-500 font-normal">(optional)</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Metric Name</Label><Input value={form.metric_name || ""} onChange={(e) => set("metric_name", e.target.value || null)} className="h-10 mt-1" /></div>
              <div><Label className="text-xs">Unit</Label>
                <select value={form.unit || ""} onChange={(e) => set("unit", e.target.value || null)} className="w-full h-10 rounded-md border border-zinc-200 px-3 text-sm mt-1">
                  <option value="">—</option>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Baseline</Label><Input value={form.baseline || ""} onChange={(e) => set("baseline", e.target.value || null)} className="h-10 mt-1" /></div>
              <div><Label className="text-xs">Goal</Label><Input value={form.goal || ""} onChange={(e) => set("goal", e.target.value || null)} className="h-10 mt-1" /></div>
              <div><Label className="text-xs">Current Result</Label><Input value={form.current_result || ""} onChange={(e) => set("current_result", e.target.value || null)} className="h-10 mt-1" /></div>
              <div><Label className="text-xs">Test Start</Label><Input type="date" value={form.test_start_date ? String(form.test_start_date).slice(0, 10) : ""} onChange={(e) => set("test_start_date", e.target.value || null)} className="h-10 mt-1" /></div>
              <div><Label className="text-xs">Review Date</Label><Input type="date" value={form.review_date ? String(form.review_date).slice(0, 10) : ""} onChange={(e) => set("review_date", e.target.value || null)} className="h-10 mt-1" /></div>
            </div>
            <div className="mt-3"><Label className="text-xs">Results / Learnings</Label><Textarea value={form.results_learnings || ""} onChange={(e) => set("results_learnings", e.target.value || null)} rows={2} className="mt-1" /></div>
          </div>

          {showResults && (
            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
              <div className="text-sm font-semibold text-zinc-900 mb-3">Actual Results</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Actual Monthly Revenue</Label><Input type="number" value={form.actual_monthly_revenue ?? ""} onChange={(e) => set("actual_monthly_revenue", e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" /></div>
                <div><Label className="text-xs">Actual Annual Revenue</Label><Input type="number" value={form.actual_annual_revenue ?? ""} onChange={(e) => set("actual_annual_revenue", e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" /></div>
                <div><Label className="text-xs">Actual Monthly Savings</Label><Input type="number" value={form.actual_monthly_savings ?? ""} onChange={(e) => set("actual_monthly_savings", e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" /></div>
                <div><Label className="text-xs">Actual Annual Savings</Label><Input type="number" value={form.actual_annual_savings ?? ""} onChange={(e) => set("actual_annual_savings", e.target.value === "" ? null : Number(e.target.value))} className="h-10 mt-1" /></div>
                <div className="col-span-2"><Label className="text-xs">Final Outcome</Label>
                  <select value={form.final_outcome || ""} onChange={(e) => set("final_outcome", e.target.value || null)} className="w-full h-10 rounded-md border border-zinc-200 px-3 text-sm mt-1">
                    <option value="">—</option>
                    {OUTCOMES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3"><Label className="text-xs">What We Learned</Label><Textarea value={form.what_we_learned || ""} onChange={(e) => set("what_we_learned", e.target.value || null)} rows={2} className="mt-1" /></div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={() => onDelete(form)} className="px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg border border-red-200 flex items-center gap-1.5" data-testid="delete-idea"><Trash2 className="w-4 h-4" />Delete</button>
            <div className="flex-1" />
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 rounded-lg">Cancel</button>
            <button type="button" onClick={() => onSave(form)} className="px-5 py-2.5 text-sm font-semibold bg-[#207024] hover:bg-[#185a1c] text-white rounded-lg" data-testid="save-idea">Save</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function GrowthBoard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortBy, setSortBy] = useState("priority");
  const [activeIdea, setActiveIdea] = useState(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickTitle, setQuickTitle] = useState("");
  const [quickNotes, setQuickNotes] = useState("");

  const auth = useMemo(() => {
    const token = localStorage.getItem("session_token");
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const fetchIdeas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/growth-board/ideas`, auth);
      setIdeas(data);
    } catch (err) {
      toast.error("Failed to load ideas");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, auth);
        setUser(me.data);
        if (me.data.role !== "Admin") { navigate("/jobs"); return; }
      } catch { navigate("/login"); return; }
      await fetchIdeas();
    })();
  }, [auth, navigate, fetchIdeas]);

  // Keep the board in sync with changes made via the AI API: poll every 30s while visible + on tab focus.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") fetchIdeas(); };
    const id = setInterval(tick, 30000);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", tick); };
  }, [fetchIdeas]);

  const filtered = useMemo(() => {
    let out = [...ideas];
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(i => (i.title || "").toLowerCase().includes(s) || (i.description || "").toLowerCase().includes(s) || (i.notes || "").toLowerCase().includes(s));
    }
    if (filterStatus !== "all") out = out.filter(i => i.status === filterStatus);
    if (filterCategory !== "all") out = out.filter(i => i.category === filterCategory);
    const nullLast = (v) => (v === null || v === undefined ? -Infinity : v);
    if (sortBy === "priority") out.sort((a, b) => nullLast(b.priority_score) - nullLast(a.priority_score));
    else if (sortBy === "impact") out.sort((a, b) => (b.est_annual_impact || 0) - (a.est_annual_impact || 0));
    else if (sortBy === "revenue") out.sort((a, b) => nullLast(b.revenue_impact) - nullLast(a.revenue_impact));
    else if (sortBy === "effort") out.sort((a, b) => nullLast(a.effort) - nullLast(b.effort));
    else if (sortBy === "newest") out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sortBy === "oldest") out.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return out;
  }, [ideas, search, filterStatus, filterCategory, sortBy]);

  const topPriorities = useMemo(() => {
    const scored = ideas.filter(i => i.status !== "done" && (i.pinned || i.priority_score !== null));
    scored.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return (b.priority_score ?? -Infinity) - (a.priority_score ?? -Infinity);
    });
    return scored.slice(0, 5);
  }, [ideas]);

  const byStatus = useMemo(() => {
    const grouped = { inbox: [], prioritized: [], doing: [], testing: [], done: [] };
    filtered.forEach(i => { if (grouped[i.status]) grouped[i.status].push(i); });
    return grouped;
  }, [filtered]);

  const summary = useMemo(() => {
    const inbox = ideas.filter(i => i.status === "inbox").length;
    const doing = ideas.filter(i => i.status === "doing").length;
    const testing = ideas.filter(i => i.status === "testing").length;
    const wins = ideas.filter(i => i.status === "done" && ["big_win", "win"].includes(i.final_outcome)).length;
    const activeImpact = ideas
      .filter(i => ["prioritized", "doing", "testing"].includes(i.status))
      .reduce((sum, i) => sum + (i.est_annual_impact || 0), 0);
    return { inbox, doing, testing, wins, activeImpact };
  }, [ideas]);

  const handleQuickAdd = async () => {
    if (!quickTitle.trim()) return;
    try {
      await axios.post(`${API}/growth-board/ideas`, { title: quickTitle.trim(), notes: quickNotes.trim() || undefined }, auth);
      setQuickTitle(""); setQuickNotes(""); setQuickAddOpen(false);
      toast.success("Idea captured");
      fetchIdeas();
    } catch { toast.error("Failed to save"); }
  };

  const handleSave = async (form) => {
    try {
      const payload = { ...form };
      delete payload.idea_id; delete payload.created_at; delete payload.updated_at; delete payload.priority_score; delete payload.est_annual_impact;
      await axios.put(`${API}/growth-board/ideas/${form.idea_id}`, payload, auth);
      toast.success("Saved");
      setActiveIdea(null);
      fetchIdeas();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Delete "${form.title}"?`)) return;
    try {
      await axios.delete(`${API}/growth-board/ideas/${form.idea_id}`, auth);
      toast.success("Deleted");
      setActiveIdea(null);
      fetchIdeas();
    } catch { toast.error("Delete failed"); }
  };

  const handleSeed = async () => {
    try {
      const { data } = await axios.post(`${API}/growth-board/seed`, {}, auth);
      if (data.seeded) { toast.success(`Seeded ${data.count} example ideas`); fetchIdeas(); }
      else toast.info(`Already have ${data.existing} ideas — skipped`);
    } catch { toast.error("Seed failed"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <Header user={user} />

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-4">
          <button onClick={() => navigate("/settings")} className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-3 text-sm"><ArrowLeft className="w-4 h-4" />Back to Settings</button>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-barlow text-2xl font-bold text-zinc-950 uppercase">Growth Board</h1>
              <p className="text-xs text-zinc-500 mt-0.5">Capture → Score → Prioritize → Execute → Test → Measure</p>
            </div>
            <button onClick={() => setQuickAddOpen(true)} className="bg-[#207024] hover:bg-[#185a1c] text-white px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-1.5 shadow-sm" data-testid="add-idea-btn"><Plus className="w-4 h-4" />Add Idea</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          <div className="bg-white border border-zinc-200 rounded-xl p-3"><div className="text-2xl font-bold text-zinc-900">{summary.inbox}</div><div className="text-xs text-zinc-500">Inbox</div></div>
          <div className="bg-white border border-zinc-200 rounded-xl p-3"><div className="text-2xl font-bold text-amber-600">{summary.doing}</div><div className="text-xs text-zinc-500">Doing</div></div>
          <div className="bg-white border border-zinc-200 rounded-xl p-3"><div className="text-2xl font-bold text-purple-600">{summary.testing}</div><div className="text-xs text-zinc-500">Testing</div></div>
          <div className="bg-white border border-zinc-200 rounded-xl p-3"><div className="text-2xl font-bold text-green-600">{summary.wins}</div><div className="text-xs text-zinc-500">Wins</div></div>
          <div className="bg-white border border-zinc-200 rounded-xl p-3 col-span-2 md:col-span-1"><div className="text-lg font-bold text-emerald-700">{money(summary.activeImpact) || "$0"}</div><div className="text-xs text-zinc-500">Active Est. Impact / yr</div></div>
        </div>

        <section className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-barlow text-lg font-bold text-zinc-900 uppercase flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" />Top Priorities</h2>
            {ideas.length === 0 && <button onClick={handleSeed} className="text-xs text-[#207024] font-semibold hover:underline flex items-center gap-1"><Sparkles className="w-3 h-3" />Seed examples</button>}
          </div>
          {topPriorities.length === 0 ? (
            <div className="bg-white border border-dashed border-zinc-300 rounded-xl p-8 text-center text-sm text-zinc-500">Score some ideas or pin one to see it here.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {topPriorities.map(i => <IdeaCard key={i.idea_id} idea={i} onOpen={setActiveIdea} />)}
            </div>
          )}
        </section>

        <div className="bg-white border border-zinc-200 rounded-xl p-3 mb-4 space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ideas..." className="pl-9 h-10" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-10 rounded-md border border-zinc-200 px-2 text-sm">
              <option value="all">All statuses</option>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="h-10 rounded-md border border-zinc-200 px-2 text-sm">
              <option value="all">All categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-10 rounded-md border border-zinc-200 px-2 text-sm">
              <option value="priority">Priority ↓</option>
              <option value="impact">$ Impact ↓</option>
              <option value="revenue">Revenue ↓</option>
              <option value="effort">Effort ↑</option>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          {STATUSES.map(s => (
            <div key={s.key} className="bg-zinc-100/60 rounded-xl p-2 min-h-[120px]">
              <div className="flex items-center justify-between px-1 py-1.5">
                <div className="text-xs font-bold text-zinc-700 uppercase">{s.label}</div>
                <div className="text-xs text-zinc-500">{byStatus[s.key].length}</div>
              </div>
              <div className="space-y-2">
                {byStatus[s.key].map(i => <IdeaCard key={i.idea_id} idea={i} onOpen={setActiveIdea} />)}
                {byStatus[s.key].length === 0 && <div className="text-center text-xs text-zinc-400 py-4">Empty</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
        <DialogContent className="max-w-md" data-testid="quick-add-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#207024]" />Capture Idea</DialogTitle>
            <DialogDescription>Save it in 10 seconds. Score it later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Idea Title</Label>
              <Input autoFocus value={quickTitle} onChange={(e) => setQuickTitle(e.target.value)} placeholder="e.g. Add warranty upsell to invoices" className="h-11 mt-1 text-base" data-testid="quick-title" onKeyDown={(e) => { if (e.key === "Enter" && quickTitle.trim()) handleQuickAdd(); }} />
            </div>
            <div>
              <Label>Quick Notes (optional)</Label>
              <Textarea value={quickNotes} onChange={(e) => setQuickNotes(e.target.value)} rows={2} placeholder="A line of context — you can flesh it out later." className="mt-1" />
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setQuickAddOpen(false)} className="flex-1 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 rounded-lg">Cancel</button>
              <button type="button" onClick={handleQuickAdd} disabled={!quickTitle.trim()} className="flex-1 py-2.5 text-sm font-semibold bg-[#207024] hover:bg-[#185a1c] disabled:bg-zinc-300 text-white rounded-lg" data-testid="quick-save">Save to Inbox</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <IdeaModal idea={activeIdea} onClose={() => setActiveIdea(null)} onSave={handleSave} onDelete={handleDelete} />

      <BottomNav activeTab="settings" user={user} />
    </div>
  );
}
